import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
// @mui material components
import Card from "@mui/material/Card";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import { useTranslation } from "react-i18next";
import SimpleModal from "components/SimpleModal";

// Material Kit 2 React examples
import Navbar from "components/Navbars";
import DefaultFooter from "examples/Footers/DefaultFooter";

// About Us page sections
import Featuring from "../../components/Featuring";
import UserHero from "./sections/UserHero";
import PricingSection from "components/sections/PricingSection";
import AppList from "components/sections/AppListSection";
import api from "services/api";
import { clearAuthStorage, isAuthenticated } from "utils/auth";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

function UserInfo() {
  const { t } = useTranslation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const pricingRef = useRef(null);
  const appListRef = useRef(null);
  const { hash } = useLocation();

  const [user, setUser] = useState(null);

  // 统一规范当前用户的套餐有效期与购买状态：
  // - 有效期早于 2000 年（如 1970 默认值）视为从未购买过套餐
  // - 仅当年份 >= 2000 且日期可解析时，才认为有有效期和历史购买记录
  const rawExpire = user?.level_expire_time;
  let normalizedExpireAt = null;
  let hasEverPurchased = false;

  if (rawExpire) {
    const d = new Date(rawExpire);
    if (!Number.isNaN(d.getTime()) && d.getFullYear() >= 2000) {
      normalizedExpireAt = rawExpire;
      hasEverPurchased = true;
    }
  }

  // 下层组件（UserHero / PricingSection 等）都应使用归一化后的用户对象，避免直接接触 1970 等占位时间
  const normalizedUser = user
    ? {
        ...user,
        level_expire_time: normalizedExpireAt,
      }
    : null;

  useEffect(() => {
    // access token 过期但 refresh token 仍可续期时，允许接口层自动刷新
    if (!isAuthenticated()) {
      clearAuthStorage();
      window.location.href = "/signin/";
    }
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        // 优先从后端获取最新的用户资料（含最新的套餐有效期）
        const res = await api.get("/auth/profile/");
        const profile = res.data;
        setUser(profile);
      } catch (err) {
        const status = err?.response?.status;

        // Token 失效/未登录：不要使用缓存数据继续留在 UserInfo，直接清理并跳转登录页
        if (status === 401 || status === 403) {
          clearAuthStorage();
          window.location.href = "/signin/";
          return;
        }

        // 其他失败（例如网络错误）：不使用本地用户资料缓存，避免把账号信息保存到浏览器存储。
      }
    };

    fetchUser();
  }, []);

  useLayoutEffect(() => {
    if (!hash) return;

    // Support accidental trailing slash like #pricing/
    const rawId = String(hash).startsWith("#") ? String(hash).slice(1) : String(hash);
    const id = rawId.endsWith("/") ? rawId.slice(0, -1) : rawId;

    if (id !== "pricing") return;

    const tryScroll = () => {
      const el = pricingRef.current || document.getElementById("pricing");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return true;
      }
      return false;
    };

    // Try multiple times because Pricing content can mount after async user/profile fetch.
    if (tryScroll()) return;
    requestAnimationFrame(() => {
      if (tryScroll()) return;
      setTimeout(() => {
        if (tryScroll()) return;
        setTimeout(() => {
          tryScroll();
        }, 350);
      }, 150);
    });
  }, [hash]);

  const scrollToPricing = () => {
    if (pricingRef.current) {
      pricingRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToAppList = () => {
    if (appListRef.current) {
      appListRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleLogoutClick = (event) => {
    // 阻止默认跳转，先弹出确认框
    if (event && event.preventDefault) {
      event.preventDefault();
    }
    setShowLogoutModal(true);
  };

  const handleLogout = async () => {
    try {
      // Clear server-side session (sessionid) so anonymous views are truly anonymous.
      await api.post("/auth/logout/", {});
    } catch (e) {
      // ignore network/logout endpoint errors and still clear local auth state
    } finally {
      clearAuthStorage();
      setShowLogoutModal(false);
      window.location.href = "/";
    }
  };

  return (
    <>
      <Navbar
        routes={routes}
        action={{
          type: "internal",
          route: "/",
          label: t("nav.logout"),
          color: "info",
          onClick: handleLogoutClick,
        }}
        transparent
        light
      />
      <UserHero
        user={normalizedUser}
        scrollToPricing={scrollToPricing}
        scrollToAppList={scrollToAppList}
      />
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <div ref={appListRef}>
          <AppList scrollToPricing={scrollToPricing} />
        </div>
        <div ref={pricingRef} id="pricing">
          <PricingSection
            userName={user?.username || user?.email || ""}
            expireAt={normalizedExpireAt}
            hasEverPurchased={hasEverPurchased}
            isLoggedIn
            isUserInfo
          />
        </div>
        <Featuring />
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
      <SimpleModal
        open={showLogoutModal}
        title={t("nav.logout")}
        cancelLabel={t("common.cancel") || "取消"}
        confirmLabel={t("nav.logout") || "登出"}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogout}
      >
        {t("nav.logoutConfirm") || "确定要退出当前账号吗？"}
      </SimpleModal>
    </>
  );
}

export default UserInfo;
